import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { fireAdsPurchase } from "@/lib/ads";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export default function PaymentResult({ outcome }) {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (outcome === "cancel") {
      setStatus("cancelled");
      return;
    }
    if (!sessionId) {
      setStatus("error");
      return;
    }

    let cancelled = false;
    const poll = async (n) => {
      if (cancelled) return;
      if (n >= 6) { setStatus("timeout"); return; }
      try {
        const { data } = await api.get(`/payments/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          fireAdsPurchase({ transactionId: sessionId, value: data.amount });
          return;
        }
        if (data.status === "expired") { setStatus("expired"); return; }
        setAttempts(n + 1);
        setTimeout(() => poll(n + 1), 2000);
      } catch {
        setStatus("error");
      }
    };
    poll(0);
    return () => { cancelled = true; };
  }, [sessionId, outcome]);

  const isSuccess = status === "paid";
  const isLoading = status === "checking";
  const Icon = isSuccess ? CheckCircle2 : isLoading ? Loader2 : XCircle;
  const headlines = {
    paid: "Payment received.",
    checking: "Confirming payment…",
    cancelled: "Payment cancelled.",
    expired: "Session expired.",
    timeout: "Still processing…",
    error: "Something went wrong.",
  };
  const subs = {
    paid: "You're all set. Your session is confirmed.",
    checking: `Attempt ${attempts + 1} of 6. Hang tight.`,
    cancelled: "No worries — you can pay any time from your portal.",
    expired: "The checkout session expired. Try again from your portal.",
    timeout: "We'll keep updating in the background. Refresh your portal in a minute.",
    error: "We couldn't verify the payment. Reach out if it doesn't update.",
  };

  return (
    <div className="min-h-screen grid place-items-center bg-[#050505] p-6" data-testid="payment-result-page">
      <div className="max-w-md w-full border border-[#27272A] bg-[#0F0F0F] p-10 text-center">
        <Icon className={`h-14 w-14 mx-auto mb-6 ${isSuccess ? "text-emerald-400" : isLoading ? "text-[#FF331F] animate-spin" : "text-zinc-400"}`} />
        <div className="text-[#FF331F] text-xs uppercase tracking-[0.3em] font-bold mb-2">Payment</div>
        <h1 className="font-display uppercase text-3xl tracking-tight">{headlines[status]}</h1>
        <p className="text-zinc-400 mt-4 text-sm">{subs[status]}</p>
        <Button onClick={() => navigate("/portal")} className="mt-8 w-full bg-[#FF331F] hover:bg-[#FF5242] text-white font-bold uppercase tracking-widest rounded-sm" data-testid="back-to-portal-btn">
          Back to portal
        </Button>
        <Link to="/" className="block mt-4 text-xs text-zinc-500 hover:text-white uppercase tracking-widest">Home</Link>
      </div>
    </div>
  );
}
